import { useMemo } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { MODULES, moduleByKey, TOTAL_SCREENS } from '@/lib/screens'
import { isLive, screenRoute, liveCount } from '@/lib/screenStatus'
import { EmptyState } from '@/components/States'
import { cn } from '@/lib/cn'

/**
 * Route'siz katalog-ekranlari — bular «rejalashtirilgan/qurilmagan» EMAS, balki
 * KARTA ICHIDAGI detal-bo'limlar: aniq bir yozuv tanlanishini talab qiladi (qaysi
 * foydalanuvchi / guruh / bot / tranzaksiya). Funksiya ota-kartada real ishlaydi
 * (masalan A3.10 «sessiyalar» = /users/:id/sessions, A5.06 «webhook» = /bots/:id ichida).
 * Katalog-plitka shu yerga tushganda foydalanuvchini to'g'ri ro'yxatga yo'naltiramiz —
 * yozuv tanlab, kartadan bo'limni ochadi. (Modul-prefiksi → ota-ro'yxat.)
 */
const DETAIL_PARENT: Record<string, { route: string; label: string }> = {
  A3: { route: '/users', label: 'Foydalanuvchilar' },
  A4: { route: '/groups', label: 'Guruhlar' },
  A5: { route: '/bots', label: 'Botlar' },
  A6: { route: '/moderation', label: 'Moderatsiya navbati' },
  A7: { route: '/support', label: 'Support navbati' },
  A8: { route: '/appeals', label: 'Apellyatsiyalar' },
  A9: { route: '/verification', label: 'Verifikatsiya arizalari' },
  A10: { route: '/finance', label: 'Moliya — tranzaksiyalar' },
  A11: { route: '/geo', label: 'Geografiya — davlatlar' },
  A14: { route: '/flags', label: 'Feature-flaglar' },
  A15: { route: '/broadcasts', label: 'Broadcast yuborishlar' },
  A19: { route: '/legal', label: 'Huquqiy so\'rovlar' },
  A23: { route: '/staff', label: 'Xodimlar' },
}

/**
 * Har qanday hali-qurilmagan ekran shu yerga marshrutlanadi (`/screen/:id`).
 * Jonli bo'lsa — o'z route'iga yo'naltiriladi. Aks holda halol «rejalashtirilgan»
 * sahifa: ekran ID/nomi/moduli + nima kutayotgani. SOXTA UI EMAS.
 */
export function PlannedScreen() {
  const { id = '' } = useParams()
  const modKey = id.split('.')[0] ?? id
  const route = screenRoute(id)
  if (route) return <Navigate to={route} replace />

  const mod = moduleByKey(modKey)
  const screen = mod?.screens.find((s) => s.id === id)
  const parent = DETAIL_PARENT[modKey]
  const isFlow = modKey === 'A1'

  return (
    <>
      <PageHeader
        title={screen ? `${id} · ${screen.title}` : id}
        screenId={id}
        actions={
          <Link
            to={mod ? `/m/${mod.key}` : '/modules'}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {mod?.title ?? 'Modullar'}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-6">
        <div className="mx-auto max-w-xl rounded-lg border border-border-default bg-surface-2 p-6 text-center">
          <p className="text-md font-medium text-primary">{screen?.title ?? id}</p>

          {parent ? (
            <>
              <p className="mt-2 text-sm text-secondary">
                Bu — <b className="text-primary">{parent.label}</b> kartasi ichidagi bo'lim.
                Ma'lumot aniq bir yozuvga tegishli, shuning uchun ro'yxatdan yozuvni tanlab,
                kartadan shu bo'limni oching.
              </p>
              <Link
                to={parent.route}
                className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-accent px-3.5 py-2 text-sm font-medium text-white hover:opacity-90"
              >
                {parent.label} ro'yxatini ochish
                <ChevronRight size={14} />
              </Link>
              <p className="mt-3 font-mono text-xs text-tertiary">Karta ichidagi bo'lim · yozuv tanlanadi</p>
            </>
          ) : isFlow ? (
            <p className="mt-2 text-sm text-secondary">
              Bu — mijoz autentifikatsiya oqimi ekrani (kirish / sessiya tugashi).
              Admin-panelда alohida sahifa emas — kirish oqimida ko'rinadi.
            </p>
          ) : (
            <p className="mt-2 text-sm text-secondary">
              Bu ekran boshqa bo'lim (karta / navbat) ichida ko'rsatiladi.
            </p>
          )}
        </div>
      </div>
    </>
  )
}

/**
 * A0 — Modullar xaritasi (614 ekranli TZ registri ustidan).
 *
 * Manba: `src/lib/screens.ts` (TZ'dan avto-generatsiya) + `screenStatus.ts`
 * (qaysi ekran JONLI). Har modul-index ekranlari ro'yxatini status bilan
 * ko'rsatadi — panel «614-shaklli» bo'ladi, har ekran implement qilingach
 * REJALASHTIRILGAN → JONLI ga o'tadi. Soxta ekran YO'Q — faqat rostini havola qiladi.
 */

function Progress({ done, total }: { done: number; total: number }) {
  const pct = total ? Math.round((done / total) * 100) : 0
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-3">
        <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
      </div>
      <span className="shrink-0 font-mono text-xs text-tertiary tabular">
        {done}/{total}
      </span>
    </div>
  )
}

export function ModulesOverview() {
  const t = useT()
  const totalDone = useMemo(
    () => MODULES.reduce((n, m) => n + liveCount(m.screens.map((s) => s.id)), 0),
    [],
  )

  return (
    <>
      <PageHeader
        title={t.nav.modules}
        screenId="A0"
        description={t.modules.overviewDesc(totalDone, TOTAL_SCREENS)}
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {MODULES.map((m) => {
            const done = liveCount(m.screens.map((s) => s.id))
            return (
              <Link
                key={m.key}
                to={`/m/${m.key}`}
                className="rounded-lg border border-border-default bg-surface-2 p-3 transition-colors hover:border-accent"
              >
                <div className="flex items-baseline gap-2">
                  <span className="font-mono text-sm text-accent">{m.key}</span>
                  <span className="min-w-0 flex-1 truncate text-base text-primary">{m.title}</span>
                </div>
                <div className="mt-2.5">
                  <Progress done={done} total={m.screens.length} />
                </div>
              </Link>
            )
          })}
        </div>
      </div>
    </>
  )
}

export function ModuleIndex() {
  const t = useT()
  const { key = '' } = useParams()
  const mod = moduleByKey(key)

  if (!mod) return <EmptyState title={t.states.notFoundTitle} />

  const done = liveCount(mod.screens.map((s) => s.id))

  return (
    <>
      <PageHeader
        title={`${mod.key} · ${mod.title}`}
        screenId={mod.key}
        description={t.modules.moduleDesc(done, mod.screens.length)}
        actions={
          <Link
            to="/modules"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.nav.modules}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="overflow-hidden rounded-md border border-border-default">
          {mod.screens.map((s, i) => {
            const live = isLive(s.id)
            const route = screenRoute(s.id)
            const row = (
              <div
                className={cn(
                  'flex items-center gap-3 px-3 py-2 text-base',
                  i > 0 && 'border-t border-border-subtle',
                  route && 'hover:bg-surface-3',
                )}
              >
                <span className="w-16 shrink-0 font-mono text-sm text-tertiary">{s.id}</span>
                <span className="min-w-0 flex-1 truncate text-primary">{s.title}</span>
                {live ? (
                  <span className="flex shrink-0 items-center gap-1 text-xs text-success">
                    <span aria-hidden>●</span>
                    {t.modules.live}
                  </span>
                ) : (
                  <span className="shrink-0 text-xs text-tertiary">{t.modules.planned}</span>
                )}
              </div>
            )
            // Jonli → o'z route'iga; qolgani → halol planned-sahifaga (`/screen/:id`).
            return (
              <Link key={s.id} to={route ?? `/screen/${s.id}`} className="block">
                {row}
              </Link>
            )
          })}
        </div>
      </div>
    </>
  )
}
